/**
 * HTS Design Tokens
 * -----------------
 * The single source of truth for the HTS visual language. These are plain
 * string/number constants so they can be referenced directly inside Griffel
 * `makeStyles` (which requires statically analyzable values) and inline styles.
 *
 * Brand anchors: HTS Blue (navy) #0D2152, HTS Aqua #00B8EB.
 *
 * Surfaces come in two systems:
 *  - `light`  — navy-tinted cool neutrals for CRUD / browse screens
 *  - `editor` — near-black navy surfaces for the tool-first plan editor
 *
 * Aqua is reserved as the single action / selection accent. Because pure aqua
 * fails contrast as text on white, `accentInk` is the darker aqua used for
 * links, active labels, and small accent text on light surfaces.
 *
 * Canonical alignment: every value here (or its `var(--hts-*)` mirror in
 * `index.css`) matches the HTS Platform Brand-Kit canonical kit
 * (`hts-platform/Brand-Kit/theme/design-tokens.ts`). When the two diverge
 * the Brand-Kit wins — this file must be updated in lockstep.
 */

export const htsColor = {
  // Brand
  navy: '#0D2152',
  navyDeep: '#0E1922',
  aqua: '#00B8EB',
  accent: '#00B8EB',
  accentInk: '#0E6FB8', // darker aqua — accessible accent text on light
  accentSoft: 'rgba(0, 184, 235, 0.12)',
  accentSoftStrong: 'rgba(0, 184, 235, 0.22)',

  // Ink (text) — resolved through CSS custom properties so dark mode flips
  // automatically. The canonical hex values live in `index.css`.
  ink: 'var(--hts-ink)',
  inkSecondary: 'var(--hts-ink-secondary)',
  inkMuted: 'var(--hts-ink-muted)',

  // Text on dark surfaces (legacy aliases — prefer ink*/tokens for new code)
  onDark: '#FFFFFF',
  onDarkSecondary: 'rgba(255, 255, 255, 0.72)',
  onDarkMuted: 'rgba(255, 255, 255, 0.50)',

  // Semantic (harmonized with the navy/aqua palette)
  // - `success` / `warning` / `info` are kept as legacy aliases (consumed by
  //   the inline Griffel styles in InstallDashboard et al).
  // - `danger` is the contract-mandated border/bg red. The companion
  //   `dangerStrong` (--hts-error-strong in CSS) is the AA text-color red
  //   (#B91C1C light / #F87171 dark). The 4.5:1 rule applies to TEXT; borders
  //   only need 3:1, so the brighter `danger` is fine for borders/badges.
  success: '#1F9D6B',
  warning: '#E0A800',
  danger: '#D8493B',
  dangerStrong: 'var(--hts-error-strong)',
  info: '#2E83C7',
} as const;

/**
 * Metric role-typography tokens (PP StatCard contract, Series 4 of 7).
 * The 27px / 650 weight / -0.02em letter-spacing triple is the contract —
 * never substitute raw 700/600/24px. CSS-side mirror lives in `index.css`
 * as `--hts-font-size-metric`, `--hts-font-weight-metric`,
 * `--hts-letter-spacing-metric`.
 */
export const htsMetric = {
  fontSize: '27px',
  fontWeight: 650,
  letterSpacing: '-0.02em',
} as const;

/**
 * Workspace surfaces.
 *
 * These reference CSS custom properties so Griffel `makeStyles` blocks inherit
 * dark-mode values automatically when `.dark` is applied to <html>. The actual
 * hex values live in `index.css`.
 */
export const htsSurface = {
  app: 'var(--hts-app-bg)', // page background
  base: 'var(--hts-surface)', // cards, panels
  raised: 'var(--hts-surface-raised)',
  sunken: 'var(--hts-surface-sunken)',
  hairline: 'var(--hts-hairline)',
  hairlineStrong: 'var(--hts-hairline-strong)',
} as const;

/** Dark editor surfaces (tool-first plan editor — used by the canvas chrome). */
export const htsEditorSurface = {
  bg: '#0E1922', // canvas backdrop
  toolbar: '#16242F',
  raised: '#22323F',
  hairline: '#2C3E4C',
} as const;

/** Light editor surfaces — the same chrome hierarchy for the light appearance
 *  (the canvas sidebar + action bar follow the resolved theme; brand-tinted
 *  grays that sit with the light theme's neutrals). */
export const htsEditorSurfaceLight = {
  bg: '#F4F7FB',   // canvas chrome backdrop
  toolbar: '#FFFFFF',
  raised: '#EDF1F7',
  hairline: '#D9E0EA',
} as const;

/** Elevation hierarchy — shadows adapt via CSS custom properties. */
export const htsElevation = {
  resting: 'var(--hts-shadow-resting)',
  raised: 'var(--hts-shadow-raised)',
  overlay: 'var(--hts-shadow-overlay)',
  editor: 'var(--hts-shadow-editor)', // floating pills on dark
} as const;

/**
 * Corner radii — canonical 4/8/12 scale shared with Project Portal + Mission
 * Control. The previous values (6/10/14) were a 2px drift that surfaced as a
 * `borderRadius: htsRadius.lg` literal inside Griffel `makeStyles` blocks
 * rendering 14px while the Fluent `Card` component and the login card
 * rendered 12px (the Fluent default winning over the HTS token). Re-aligned
 * to canonical 4/8/12 on 2026-07-29 (button-align-test).
 */
export const htsRadius = {
  sm: '4px',
  md: '8px',
  lg: '12px',
  pill: '999px',
} as const;

export const htsSpace = {
  xs: '4px',
  sm: '8px',
  md: '12px',
  lg: '16px',
  xl: '24px',
  xxl: '32px',
} as const;

/**
 * Type families. Self-hosted variable fonts (see main.tsx imports).
 * `display` previously used Archivo Variable as a paired display face;
 * reverted to Inter-only 2026-09-19 to match the canonical Brand-Kit
 * (hts-platform/Brand-Kit/theme/design-tokens.ts), which defines a single
 * Inter family with no separate display face.
 */
export const htsFont = {
  display: "'Inter', 'Inter Variable', system-ui, -apple-system, 'Segoe UI', sans-serif",
  body: "'Inter', 'Inter Variable', system-ui, -apple-system, 'Segoe UI', sans-serif",
  mono: "'JetBrains Mono Variable', ui-monospace, 'SF Mono', 'Cascadia Mono', monospace",
} as const;

/** A deliberate type scale (size / line-height / weight / tracking). */
export const htsType = {
  display: { fontSize: '30px', lineHeight: '36px', fontWeight: 700, letterSpacing: '-0.02em' },
  h1: { fontSize: '24px', lineHeight: '30px', fontWeight: 700, letterSpacing: '-0.015em' },
  h2: { fontSize: '19px', lineHeight: '26px', fontWeight: 650, letterSpacing: '-0.01em' },
  h3: { fontSize: '16px', lineHeight: '22px', fontWeight: 600, letterSpacing: '-0.005em' },
  body: { fontSize: '14px', lineHeight: '20px', fontWeight: 400 },
  small: { fontSize: '12.5px', lineHeight: '17px', fontWeight: 400 },
  caption: { fontSize: '11.5px', lineHeight: '15px', fontWeight: 500, letterSpacing: '0.02em' },
  // Mono label for technical data (device IDs, BOM quantities, coordinates)
  mono: { fontFamily: "'JetBrains Mono Variable', ui-monospace, monospace", fontFeatureSettings: "'tnum' 1", letterSpacing: '-0.01em' },
} as const;

/**
 * Font-size scale — flat string map mirroring `--hts-font-size-*` CSS custom
 * properties in `index.css`. Use this inside Griffel `makeStyles` blocks
 * (which need static values) instead of writing `fontSize: '12px'` literals.
 * For CSS-var consumers (raw elements, third-party surfaces) prefer the
 * `var(--hts-font-size-*)` form.
 */
export const htsFontSize = {
  xs: '12px',   // --hts-font-size-xs   — small labels, captions, badges
  sm: '14px',   // --hts-font-size-sm   — body text (the default)
  base: '16px', // --hts-font-size-base — emphasis, card titles
  lg: '18px',   // --hts-font-size-lg   — section titles
  xl: '20px',   // --hts-font-size-xl   — page-level emphasis
  '2xl': '24px', // --hts-font-size-2xl — h1 / display
} as const;

/** Convenience: an uppercase eyebrow/label treatment used on section headers. */
export const htsEyebrow = {
  fontFamily: htsFont.display,
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '0.08em',
  textTransform: 'uppercase' as const,
  color: htsColor.inkMuted,
};

// ============================================================================
// Card contract (Series 2 of 7) — 7-variant surface tokens
// ----------------------------------------------------------------------------
// The Card contract specifies 7 variants. The 5 of 6 SS Card call sites that
// hand-rolled `borderRadius: htsRadius.lg, padding: '24px', boxShadow:
// htsElevation.resting` now consume these helpers from `getCardStyles` in
// `components/layout/Card.tsx`. Tokens below are the surface semantics each
// variant maps to; the helper centralizes the per-variant Griffel output so
// any future radius or padding change ships from one place.
// ============================================================================

/**
 * Card variant names matching the cross-hub contract. SS keeps Fluent's
 * `<Card>` primitive (the library is the aligned contract surface, not the
 * variant count), but the variant *name* is the cross-hub API: any consumer
 * that wants `variant="tile"` or `variant="login"` is portable.
 */
export type HtsCardVariant =
  | 'default'      // raised surface (the workhorse — ProjectCard, DeviceCard, etc.)
  | 'elevated'     // popover/modal — no border, stronger shadow
  | 'flat'         // sunken bg, no border, no shadow
  | 'interactive'  // hover/active states; clickable (consumers add onClick)
  | 'tile'         // dashboard KPI container; metric-friendly, 16px pad
  | 'login'        // forced white, 40px pad, custom shadow (login standard)
  | 'outline';     // transparent bg, 1px border (settings panels, fieldset-like)

export const htsCardVariant: Readonly<Record<HtsCardVariant, {
  padding: string;
  radius: string;
  shadow: string;
  bg: string;
  border: string;
}>> = {
  default:    { padding: '24px', radius: '12px', shadow: htsElevation.resting, bg: htsSurface.base,    border: `1px solid ${htsSurface.hairline}` },
  elevated:   { padding: '24px', radius: '12px', shadow: htsElevation.raised,   bg: htsSurface.raised,  border: 'none' },
  flat:       { padding: '24px', radius: '12px', shadow: 'none',                bg: htsSurface.sunken,  border: 'none' },
  interactive:{ padding: '24px', radius: '12px', shadow: htsElevation.resting, bg: htsSurface.base,    border: `1px solid ${htsSurface.hairline}` },
  tile:       { padding: '16px', radius: '12px', shadow: htsElevation.resting, bg: htsSurface.base,    border: `1px solid ${htsSurface.hairline}` },
  login:      { padding: '40px', radius: '12px', shadow: '0 10px 30px rgba(13, 33, 82, 0.08)', bg: '#FFFFFF', border: 'none' },
  outline:    { padding: '24px', radius: '12px', shadow: 'none',                bg: 'transparent',     border: `1px solid ${htsSurface.hairlineStrong}` },
};

/**
 * The login-card custom shadow, lifted to a constant so the value isn't
 * re-typed in `LoginPage.tsx`. Mirrors `--hts-shadow-login` (which is
 * defined in `index.css` for the `var()`-consumers).
 */
export const htsShadowLogin = '0 10px 30px rgba(13, 33, 82, 0.08)';

// ============================================================================
// StatCard contract (Series 4 of 7) — 5-tone value colors + subTone
// ----------------------------------------------------------------------------
// PP is the contract reference; SS adopts the same `tone` enum. The 4 hubs
// converged on these hex values (the `color.status.*` block of the Brand-Kit
// canonical theme). SS keeps them as a flat string map for Griffel consumers.
// ============================================================================

/**
 * StatCard tone — the value color. Separate from the subTone (delta) color
 * per the contract: never color value AND delta with the same tone.
 */
export type HtsStatTone = 'default' | 'positive' | 'warning' | 'critical' | 'info';

/**
 * Tone color lookup. Light + dark both resolve via the same CSS var (the
 * `var()` flips with `.dark`). Values mirror the Brand-Kit `color.status`
 * block, which is the cross-app canonical.
 *
 *   tone     | light text-color (light bg) | dark text-color (dark bg)
 *   -------- | --------------------------- | ------------------------
 *   default  | --hts-ink                   | --hts-ink
 *   positive | --hts-success-text          | --hts-success-text
 *   warning  | --hts-warning-text          | --hts-warning-text
 *   critical | --hts-error-strong          | --hts-error-strong
 *   info     | --hts-info-text             | --hts-info-text
 */
export const htsStatToneColor: Readonly<Record<HtsStatTone, string>> = {
  default:  htsColor.ink,
  positive: 'var(--hts-success-text)',
  warning:  'var(--hts-warning-text)',
  critical: 'var(--hts-error-strong)',
  info:     'var(--hts-info-text)',
};

// ============================================================================
// Skeleton contract (Series 7 of 7) — base + sheen for light + dark
// ----------------------------------------------------------------------------
// PP-canonical: base #DDE3EC (light) / #1A2030 (dark); sheen is the moving
// gradient highlight. Skeleton-bg vs page-bg contrast must be ≥1.3:1
// (WCAG 1.4.11 non-text content). Verified:
//   light base #DDE3EC on page #F1F4F9 = 1.42:1 ✓
//   dark  base #1A2030 on page #0A0D13 = 1.85:1 ✓
// ============================================================================

export const htsSkeleton = {
  base:  'var(--skeleton-base)',   // the resting block color
  sheen: 'var(--skeleton-sheen)',  // the moving highlight
  radius: '4px',                        // the line/card/table-row default
} as const;
